import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@clevis/shared-ui";

// Who's on the board: everyone, nobody yet, or a mix picked by department
// (tick a department for all its people) with individuals from other
// departments ("the service team plus Pat from production"). Remembered per
// device by the page that uses it.

export interface TechSelection {
  /** Empty = everyone (within the page's department filter). */
  uids: string[];
  /** Only jobs nobody has yet. */
  noTech: boolean;
}
export const EVERYONE: TechSelection = { uids: [], noTech: false };

export function TechPicker({
  members,
  departments,
  value,
  onChange,
  techWord = "techs",
  className,
}: {
  members: Array<{ uid: string; name: string }>;
  departments: Array<{ id: string; name: string; memberUids: string[]; managerUids: string[] }>;
  value: TechSelection;
  onChange: (next: TechSelection) => void;
  techWord?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  // The popover hangs off whichever side of the button has room.
  const [side, setSide] = useState<"left" | "right">("right");
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = useMemo(() => new Set(value.uids.filter((u) => members.some((m) => m.uid === u))), [value.uids, members]);
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  const groups = useMemo(() => {
    const placed = new Set<string>();
    const out = departments
      .map((d) => {
        const people = members.filter((m) => d.memberUids.includes(m.uid) || d.managerUids.includes(m.uid)).sort(byName);
        people.forEach((p) => placed.add(p.uid));
        return { id: d.id, name: d.name, people };
      })
      .filter((g) => g.people.length);
    const rest = members.filter((m) => !placed.has(m.uid)).sort(byName);
    if (rest.length) out.push({ id: "", name: departments.length ? "Not in a department" : "Everyone", people: rest });
    return out;
  }, [departments, members]);

  const label = value.noTech
    ? `No ${techWord.replace(/s$/, "")} yet`
    : selected.size === 0
      ? `All ${techWord}`
      : (() => {
          // Whole departments by name, then the odd people.
          const names: string[] = [];
          let left = new Set(selected);
          for (const g of groups) {
            if (g.id && g.people.length && g.people.every((p) => left.has(p.uid))) {
              names.push(g.name);
              g.people.forEach((p) => left.delete(p.uid));
            }
          }
          left = new Set([...left]);
          const people = members.filter((m) => left.has(m.uid)).map((m) => m.name.split(/\s+/)[0]!);
          const all = [...names, ...people];
          return all.length <= 3 ? all.join(" + ") : `${all.slice(0, 2).join(" + ")} + ${all.length - 2} more`;
        })();

  const set = (uids: Set<string>) => onChange({ uids: [...uids], noTech: false });
  const togglePerson = (uid: string) => {
    const n = new Set(selected);
    if (n.has(uid)) n.delete(uid);
    else n.add(uid);
    set(n);
  };
  const toggleGroup = (people: Array<{ uid: string }>) => {
    const n = new Set(selected);
    const allIn = people.every((p) => n.has(p.uid));
    people.forEach((p) => (allIn ? n.delete(p.uid) : n.add(p.uid)));
    set(n);
  };

  return (
    <div ref={box} className={cn("relative", className)}>
      <button
        type="button"
        className={cn("flex items-center gap-1 rounded-lg border border-steel/30 bg-white px-2.5 py-1 text-xs text-ink hover:border-steel/60", open && "border-brandorange ring-1 ring-brandorange")}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          const r = box.current?.getBoundingClientRect();
          if (r) setSide(r.left + 288 > window.innerWidth && r.right - 288 >= 0 ? "right" : "left");
          setOpen((v) => !v);
        }}
      >
        <span className="max-w-48 truncate">{label}</span>
        <ChevronDown size={13} className={cn("text-steel transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div role="dialog" aria-label={`Which ${techWord}`} className={cn("absolute z-40 mt-1 max-h-[70vh] w-72 overflow-y-auto rounded-xl border border-steel/25 bg-white p-2 text-sm shadow-xl shadow-ink/10", side === "right" ? "right-0" : "left-0")}>
          <div className="mb-1 flex gap-1 text-xs">
            <button type="button" className={cn("rounded-md px-2 py-1", !value.noTech && selected.size === 0 ? "bg-ink text-white" : "text-steel hover:bg-cream")} onClick={() => onChange(EVERYONE)}>
              All {techWord}
            </button>
            <button type="button" className={cn("rounded-md px-2 py-1", value.noTech ? "bg-ink text-white" : "text-steel hover:bg-cream")} onClick={() => onChange({ uids: [], noTech: true })}>
              No {techWord.replace(/s$/, "")} yet
            </button>
            {selected.size ? (
              <button type="button" className="ml-auto rounded-md px-2 py-1 text-steel hover:bg-cream" onClick={() => onChange(EVERYONE)}>
                Clear
              </button>
            ) : null}
          </div>
          {groups.map((g) => {
            const allIn = g.people.every((p) => selected.has(p.uid));
            const someIn = !allIn && g.people.some((p) => selected.has(p.uid));
            return (
              <div key={g.id || "rest"} className="border-t border-steel/10 py-1">
                <label className="flex cursor-pointer items-center gap-2 px-1 py-0.5 text-xs font-semibold text-ink">
                  <input
                    type="checkbox"
                    checked={allIn}
                    ref={(el) => el && (el.indeterminate = someIn)}
                    onChange={() => toggleGroup(g.people)}
                  />
                  {g.name}
                  <span className="font-normal text-steel">({g.people.length})</span>
                </label>
                {g.people.map((p) => (
                  <label key={p.uid} className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-0.5 pl-6 text-xs text-ink hover:bg-cream">
                    <input type="checkbox" checked={selected.has(p.uid)} onChange={() => togglePerson(p.uid)} />
                    {p.name}
                  </label>
                ))}
              </div>
            );
          })}
          {groups.length === 0 ? <p className="px-1 py-2 text-xs text-steel">Nobody on the team yet.</p> : null}
        </div>
      ) : null}
    </div>
  );
}
